import { createElement, ReactElement, ReactNode } from 'react';

type Props = Record<string, unknown>;

/**
 * The tags a text may have (markdown and the HTML in it). Anything else becomes a plain span with its text: the HTML
 * of markdown-to-jsx 6 takes any tag and attribute, so <iframe src="javascript:..."> would run a script (TASK-318).
 */
const ALLOWED_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'del',
    'div',
    'em',
    'footer',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'input',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'span',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
    'ul'
]);

/** The tags whose content is code or a control, not text: left out with it */
const DROPPED_WITH_CONTENT = new Set([
    'embed',
    'iframe',
    'math',
    'noscript',
    'object',
    'script',
    'select',
    'style',
    'svg',
    'template',
    'textarea',
    'title'
]);

const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:'];
const TEXT_ALIGNS = ['left', 'center', 'right'];
const NO_OPENER = 'noopener noreferrer';

/** A link or an image address: http(s), mailto or relative; the browser's own parsing (it drops tabs, new lines) */
export const toSafeUrl = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;

    try {
        const { protocol } = new URL(value.trim(), window.location.origin);

        return SAFE_PROTOCOLS.includes(protocol) ? value.trim() : undefined;
    } catch (error) {
        // An address the browser cannot parse is not a link
        return undefined;
    }
};

const toText = (value: unknown) => (typeof value === 'string' ? value : undefined);

const pickLinkProps = ({ href, target }: Props): Props => {
    const linkTarget = toText(target);

    // A new tab never gets window.opener
    return { href: toSafeUrl(href), target: linkTarget, rel: linkTarget ? NO_OPENER : undefined };
};

const pickTagProps = (type: string, props: Props): Props => {
    if (type === 'a') return pickLinkProps(props);
    if (type === 'img') return { src: toSafeUrl(props.src), alt: toText(props.alt) };
    if (type === 'ol') return { start: typeof props.start === 'number' ? props.start : undefined };
    // The column alignment of a markdown table
    if (type === 'th' || type === 'td') {
        const textAlign = (props.style as { textAlign?: unknown } | undefined)?.textAlign;

        return { style: TEXT_ALIGNS.includes(textAlign as string) ? { textAlign } : undefined };
    }

    return {};
};

/**
 * The createElement of markdown-to-jsx: only the allowed tags with their known attributes (no id, class, style, on*
 * or any other); the text stays text (React escapes it).
 */
export const createSafeElement = (type: string, props: Props | null, ...children: ReactNode[]): ReactElement => {
    const tag = String(type).toLowerCase();
    const key = props?.key as string | number | undefined;

    if (DROPPED_WITH_CONTENT.has(tag)) return createElement('span', { key });
    if (!ALLOWED_TAGS.has(tag)) return createElement('span', { key }, ...children);
    // The only input markdown makes: the box of a task list item
    if (tag === 'input') {
        return props?.type === 'checkbox'
            ? createElement('input', { key, type: 'checkbox', checked: !!props.checked, readOnly: true })
            : createElement('span', { key });
    }

    return createElement(tag, { key, ...pickTagProps(tag, props || {}), title: toText(props?.title) }, ...children);
};
